import React, { useEffect, useState } from "react";
import AdminHeader from "./Components/AdminHeader";
import AdminSidebar from "./Components/AdminSidebar";
import HowToItemModal from "./Components/HowToItemModal";
import HowToCategoryModal from "./Components/HowToCategoryModal";
import api from "../../api";
import {IMAGE_URL} from "../../path";
import "../../styles/Login.scss";

interface HowToCategory {
    id: number;
    title: string;
    slug: string;
    header: string | null;
}

interface HowToItem {
    id: number;
    how_to_category_id: number;
    title: string;
    description: string;
    image: string | null;
    display_order: number;
    active: boolean;
    category?: HowToCategory;
}

const CATEGORY_ORDER = ["health", "cooking", "cleaning"];

const AdminHowTo = () => {
    const [categories, setCategories] = useState<HowToCategory[]>([]);
    const [items, setItems] = useState<HowToItem[]>([]);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [success, setSuccess] = useState("");

    const [modalOpen, setModalOpen] = useState(false);
    const [selectedItem, setSelectedItem] =
        useState<HowToItem | null>(null);

    const [categoryModalOpen, setCategoryModalOpen] =
        useState(false);

    const [selectedCategory, setSelectedCategory] =
        useState<HowToCategory | null>(null);

    const [movingItemId, setMovingItemId] =
        useState<number | null>(null);

    const [deletingItemId, setDeletingItemId] =
        useState<number | null>(null);

    useEffect(() => {
        fetchHowTo();
    }, []);

    const fetchHowTo = async () => {
        try {
            setLoading(true);
            setError("");

            const [
                categoriesResponse,
                itemsResponse,
            ] = await Promise.all([
                api.get("/admin/how-to-categories"),
                api.get("/admin/how-to-items"),
            ]);

            const categoriesData =
                categoriesResponse.data?.data ??
                categoriesResponse.data ??
                [];

            const itemsData =
                itemsResponse.data?.data ??
                itemsResponse.data ??
                [];

            setCategories(categoriesData);
            setItems(itemsData);
        } catch (err: any) {
            console.error("Failed to load How To:", err);

            setError(
                err.response?.data?.message ||
                "Unable to load How To content. Please try again."
            );
        } finally {
            setLoading(false);
        }
    };

    const getCategoryIndex = (
        category: HowToCategory | undefined
    ) => {
        if (!category) {
            return CATEGORY_ORDER.length;
        }

        const index = CATEGORY_ORDER.indexOf(category.slug);

        return index === -1
            ? CATEGORY_ORDER.length
            : index;
    };

    const sortedCategories = [...categories].sort(
        (a, b) =>
            getCategoryIndex(a) - getCategoryIndex(b)
    );

    const sortedItems = [...items].sort((a, b) => {
        const categoryA = categories.find(
            (category) =>
                category.id === a.how_to_category_id
        );

        const categoryB = categories.find(
            (category) =>
                category.id === b.how_to_category_id
        );

        const categoryOrderA =
            getCategoryIndex(categoryA);

        const categoryOrderB =
            getCategoryIndex(categoryB);

        if (categoryOrderA !== categoryOrderB) {
            return categoryOrderA - categoryOrderB;
        }

        return a.display_order - b.display_order;
    });

    const getCategoryName = (categoryId: number) => {
        const category = categories.find(
            (item) => item.id === categoryId
        );

        return category?.title || "—";
    };

    const getItemsForCategory = (categoryId: number) => {
        return sortedItems.filter(
            (item) =>
                item.how_to_category_id === categoryId
        );
    };

    const handleAddItem = () => {
        setSelectedItem(null);
        setModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleEditItem = (item: HowToItem) => {
        setSelectedItem(item);
        setModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setSelectedItem(null);
    };

    const handleItemSaved = async () => {
        const wasEditing = selectedItem !== null;

        handleCloseModal();
        await fetchHowTo();

        setSuccess(
            wasEditing
                ? "How To item updated successfully."
                : "How To item added successfully."
        );
    };

    const handleEditCategory = (
        category: HowToCategory
    ) => {
        setSelectedCategory(category);
        setCategoryModalOpen(true);
        setError("");
        setSuccess("");
    };

    const handleCloseCategoryModal = () => {
        setCategoryModalOpen(false);
        setSelectedCategory(null);
    };

    const handleCategorySaved = async () => {
        handleCloseCategoryModal();
        await fetchHowTo();

        setSuccess(
            "How To category header updated successfully."
        );
    };

    const handleDeleteItem = async (item: HowToItem) => {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${item.title}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            setDeletingItemId(item.id);
            setError("");
            setSuccess("");

            await api.delete(
                `/admin/how-to-items/${item.id}`
            );

            await fetchHowTo();

            setSuccess(
                "How To item deleted successfully."
            );
        } catch (err: any) {
            console.error(
                "Failed to delete How To item:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to delete How To item. Please try again."
            );
        } finally {
            setDeletingItemId(null);
        }
    };

    const handleMoveItem = async (
        item: HowToItem,
        direction: "up" | "down"
    ) => {
        try {
            setMovingItemId(item.id);
            setError("");
            setSuccess("");

            await api.post(
                `/admin/how-to-items/${item.id}/move`,
                {
                    direction,
                }
            );

            await fetchHowTo();
        } catch (err: any) {
            console.error(
                "Failed to move How To item:",
                err
            );

            setError(
                err.response?.data?.message ||
                "Unable to change How To item order. Please try again."
            );
        } finally {
            setMovingItemId(null);
        }
    };

    const canMoveItem = (
        item: HowToItem,
        direction: "up" | "down"
    ) => {
        const categoryItems = getItemsForCategory(
            item.how_to_category_id
        );

        const index = categoryItems.findIndex(
            (categoryItem) =>
                categoryItem.id === item.id
        );

        if (direction === "up") {
            return index > 0;
        }

        return (
            index >= 0 &&
            index < categoryItems.length - 1
        );
    };

    return (
        <div className="admin-page">
            <AdminHeader />

            <div className="admin-body">
                <AdminSidebar />

                <main className="admin-content">
                    <div className="admin-users">

                        {/* PAGE HEADER */}
                        <div className="admin-page-header">
                            <div>
                                <h1>How To</h1>

                                <p>
                                    Manage How To content for
                                    Health, Cooking and Cleaning.
                                </p>
                            </div>

                            <button
                                type="button"
                                className="admin-primary-button"
                                onClick={handleAddItem}
                                disabled={
                                    categories.length === 0
                                }
                            >
                                Add How To
                            </button>
                        </div>

                        {error && (
                            <div className="form-error">
                                {error}
                            </div>
                        )}

                        {success && (
                            <div className="form-success">
                                {success}
                            </div>
                        )}

                        {loading ? (
                            <p>Loading How To content...</p>
                        ) : (
                            <>
                                {/* CATEGORY HEADERS */}
                                <div className="admin-section">
                                    <div className="admin-section-header">
                                        <div>
                                            <h2>
                                                Category Headers
                                            </h2>

                                            <p>
                                                Manage the header
                                                image for each
                                                How To category.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="admin-users-table">
                                        <table>
                                            <thead>
                                            <tr>
                                                <th>
                                                    Category
                                                </th>

                                                <th>
                                                    Header
                                                </th>

                                                <th>
                                                    Actions
                                                </th>
                                            </tr>
                                            </thead>

                                            <tbody>
                                            {sortedCategories.map(
                                                (category) => (
                                                    <tr
                                                        key={
                                                            category.id
                                                        }
                                                    >
                                                        <td>
                                                            {
                                                                category.title
                                                            }
                                                        </td>

                                                        <td>
                                                            {category.header ? (
                                                                <img
                                                                    src={`${IMAGE_URL}/${category.header}`}
                                                                    alt={`${category.title} header`}
                                                                    className="admin-table-image"
                                                                />
                                                            ) : (
                                                                "—"
                                                            )}
                                                        </td>

                                                        <td>
                                                            <div className="admin-user-actions">
                                                                <button
                                                                    type="button"
                                                                    className="admin-table-button"
                                                                    onClick={() =>
                                                                        handleEditCategory(
                                                                            category
                                                                        )
                                                                    }
                                                                >
                                                                    Edit
                                                                </button>
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )
                                            )}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>

                                {/* HOW TO ITEMS */}
                                <div className="admin-section">
                                    <div className="admin-section-header">
                                        <div>
                                            <h2>
                                                How To Items
                                            </h2>

                                            <p>
                                                Manage the How To
                                                content for each
                                                category.
                                            </p>
                                        </div>
                                    </div>

                                    {items.length === 0 ? (
                                        <div className="admin-empty-state">
                                            No How To items have
                                            been added yet.
                                        </div>
                                    ) : (
                                        <div className="admin-users-table">
                                            <table>
                                                <thead>
                                                <tr>
                                                    <th>
                                                        Category
                                                    </th>

                                                    <th>
                                                        Title
                                                    </th>

                                                    <th>
                                                        Active
                                                    </th>

                                                    <th>
                                                        Order
                                                    </th>

                                                    <th>
                                                        Actions
                                                    </th>
                                                </tr>
                                                </thead>

                                                <tbody>
                                                {sortedItems.map(
                                                    (item) => (
                                                        <tr
                                                            key={
                                                                item.id
                                                            }
                                                        >
                                                            <td>
                                                                {getCategoryName(
                                                                    item.how_to_category_id
                                                                )}
                                                            </td>

                                                            <td>
                                                                {
                                                                    item.title
                                                                }
                                                            </td>

                                                            <td>
                                                                    <span
                                                                        className={`admin-role ${
                                                                            item.active
                                                                                ? "admin-role-admin"
                                                                                : "admin-role-editor"
                                                                        }`}
                                                                    >
                                                                        {item.active
                                                                            ? "Active"
                                                                            : "Inactive"}
                                                                    </span>
                                                            </td>

                                                            <td>
                                                                <div className="admin-user-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        onClick={() =>
                                                                            handleMoveItem(
                                                                                item,
                                                                                "up"
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !canMoveItem(
                                                                                item,
                                                                                "up"
                                                                            ) ||
                                                                            movingItemId !==
                                                                            null ||
                                                                            deletingItemId !==
                                                                            null
                                                                        }
                                                                        aria-label={`Move ${item.title} up`}
                                                                    >
                                                                        ↑
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        onClick={() =>
                                                                            handleMoveItem(
                                                                                item,
                                                                                "down"
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            !canMoveItem(
                                                                                item,
                                                                                "down"
                                                                            ) ||
                                                                            movingItemId !==
                                                                            null ||
                                                                            deletingItemId !==
                                                                            null
                                                                        }
                                                                        aria-label={`Move ${item.title} down`}
                                                                    >
                                                                        ↓
                                                                    </button>
                                                                </div>
                                                            </td>

                                                            <td>
                                                                <div className="admin-user-actions">
                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button"
                                                                        onClick={() =>
                                                                            handleEditItem(
                                                                                item
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            movingItemId !==
                                                                            null ||
                                                                            deletingItemId !==
                                                                            null
                                                                        }
                                                                    >
                                                                        Edit
                                                                    </button>

                                                                    <button
                                                                        type="button"
                                                                        className="admin-table-button admin-table-button-danger"
                                                                        onClick={() =>
                                                                            handleDeleteItem(
                                                                                item
                                                                            )
                                                                        }
                                                                        disabled={
                                                                            deletingItemId ===
                                                                            item.id ||
                                                                            movingItemId !==
                                                                            null
                                                                        }
                                                                    >
                                                                        {deletingItemId ===
                                                                        item.id
                                                                            ? "Deleting..."
                                                                            : "Delete"}
                                                                    </button>
                                                                </div>
                                                            </td>
                                                        </tr>
                                                    )
                                                )}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                </main>
            </div>

            {/* HOW TO ITEM MODAL */}
            {modalOpen && (
                <HowToItemModal
                    item={selectedItem}
                    categories={categories}
                    onClose={handleCloseModal}
                    onSaved={handleItemSaved}
                />
            )}

            {/* CATEGORY HEADER MODAL */}
            {categoryModalOpen && selectedCategory && (
                <HowToCategoryModal
                    category={selectedCategory}
                    onClose={handleCloseCategoryModal}
                    onSaved={handleCategorySaved}
                />
            )}
        </div>
    );
};

export default AdminHowTo;
